import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { Observable, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AdminAccountsFoldersApi } from '../../../../api/adminAccountsFolders/adminAccountsFolders.api';
import { AdminAccountFolderApiModel } from '../../../../api/adminAccountsFolders/models/adminAccountFolderApiModel';

import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AccountModel } from '../../../../Core/Users/models/account.model';

import { Folder } from '../../models/folder.model';
import { CopyMentionsToAccountService } from '../../services/copy-mentions-to-account.service';

import { BulkActionFormComponent } from '../MonitoringBulkActionDialog/bulk-action-form.component';

export class FolderBulkActionParameters{
    showAutoCreateOption: boolean;
}
// the actionValue for this form
export interface AddAccountBulkActionModel {
    account: AccountModel;
    folder: Folder;
}

@Component({
    selector: 'agpr-bulk-action-form-account',
    templateUrl: './bulk-action-form-account.component.html',
    styleUrls: ['./bulk-action-form-account.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BulkActionFormAccountComponent),
            multi: true
        }],
    standalone: false
})
export class BulkActionFormAccountComponent extends BulkActionFormComponent implements ControlValueAccessor, OnDestroy {

    id: string = 'bulk_action_account';
    selectedFolder: Folder;
    customOptions: AutocompleteCustomOption[] = [];
    folderList$: Observable<Folder[]>;

    useExistingFolder: boolean = false;
    foldersLoading: boolean = false;
    newFolderName: string = '';

    selectedAccount: AccountModel;

    private subscriptions: Subscription[] = [];

    @Input() tooltip: string;
    @Input() heading: string = 'Mention.details.folders';
    @Input() set parameters(theParameters: FolderBulkActionParameters) {
        if (theParameters.showAutoCreateOption) {
            this.customOptions = [
                {
                    label: 'Mention.editDialog.folder.createNewFolder',
                    action: (searchString: string) => {
                        const adminFolder = {
                            name: searchString
                        } as AdminAccountFolderApiModel;

                        this.adminAccountsFoldersApi.createAdminAccountFolder(this.selectedAccount.id, adminFolder).subscribe(
                            (folderId) => {

                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'MentionFoldersService.folderCreatedHeading',
                                    detail: this.localizationService.transform('MentionFoldersService.folderCreatedMessage', { 'name': searchString })
                                });

                                this.selectedFolder = new Folder({name: searchString, id: folderId});
                                this.copyMentionsToAccountService.appendAccountFolder(this.selectedFolder);
                                this.folderSelected(this.selectedFolder);
                            },
                            err => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'MentionFoldersService.folderCreateErrorHeading',
                                    detail: 'MentionFoldersService.folderCreateErrorGenericMessage'
                                });
                            }
                        );
                    },
                    appearCondition: AppearCondition.noExactMatchInSuggestions,
                    clearOnSelect: true
                }
            ];
        } else {
            this.customOptions = [];
        }
    };

    constructor(
        public copyMentionsToAccountService: CopyMentionsToAccountService,
        private adminAccountsFoldersApi: AdminAccountsFoldersApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {
        super();

        this.subscriptions.push(
            this.copyMentionsToAccountService.getFoldersLoading().subscribe(
                loading => this.foldersLoading = loading
            )
        );
        this.folderList$ = copyMentionsToAccountService.getAccountFolders();
    }

    writeValue(folder: Folder): void {
        this.selectedFolder = folder;
    }

    addAccount(event) {
        this.selectedAccount = event;
        this.selectedFolder = null;
        this.onModelChange({
            folder: this.selectedFolder,
            account: this.selectedAccount
        });
        if (this.selectedAccount) {
            this.copyMentionsToAccountService.getAllAccountFolders(this.selectedAccount.id);
        }
    }

    folderSelected(folder: Folder): void {
        this.selectedFolder = folder;
        this.onModelChange({
            folder: this.selectedFolder,
            account: this.selectedAccount
        });
    }

    /**
     * Cleans up the subscriptions and providers when the service is terminated
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
